import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { usePermission } from '../../../hooks/usePermission';
import { FaPlus, FaTrash, FaUserShield, FaCheck, FaTimes, FaEye, FaEdit } from 'react-icons/fa';

const AdminManagement = () => {
    const { can, isSuperAdmin } = usePermission();
    const [admins, setAdmins] = useState([]);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState({});
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        role_id: '',
        custom_role_name: '',
        custom_permissions: []
    });
    const [isCustomRole, setIsCustomRole] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchAdmins();
        fetchRoles();
        fetchPermissions();
    }, []);

    const fetchAdmins = async () => {
        try {
            const res = await axios.get(`${SERVER}/admin/users`, { withCredentials: true }); 
            setAdmins(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRoles = async () => {
        try {
            const res = await axios.get(`${SERVER}/roles`, { withCredentials: true });
            setRoles(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPermissions = async () => {
        try {
            const res = await axios.get(`${SERVER}/roles/permissions`, { withCredentials: true });
            setPermissions(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Open Modal for Create
    const openCreateModal = () => {
        setIsEditMode(false);
        setEditingId(null);
        setFormData({ email: '', password: '', role_id: '', custom_role_name: '', custom_permissions: [] });
        setIsCustomRole(false);
        setShowPermissions(false);
        setShowModal(true);
        setMsg({ type: '', text: '' });
    };

    // Open Modal for Edit
    const openEditModal = async (adminId) => {
        setLoading(true);
        try {
            const res = await axios.get(`${SERVER}/admin/users/${adminId}`, { withCredentials: true });
            const admin = res.data;
            
            setIsEditMode(true);
            setEditingId(adminId);
            
            // Determine if custom role
            const isCustom = admin.role_name && admin.role_name.startsWith('Custom');
            
            setFormData({
                email: admin.email,
                password: '', // Don't show password
                role_id: isCustom ? 'custom' : (admin.role_id || ''),
                custom_role_name: isCustom ? admin.role_name : '',
                custom_permissions: admin.permissions || []
            });
            setIsCustomRole(isCustom);
            setShowPermissions(true); // Always show permissions in edit mode
            setShowModal(true);
            setMsg({ type: '', text: '' });
        } catch (err) {
            console.error("Failed to fetch admin details", err);
            alert("Could not load admin details");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (name === 'role_id') {
           const selectedRole = roles.find(r => r.id === parseInt(value));
           const isCustom = (selectedRole?.name && selectedRole.name.startsWith('Custom')) || value === 'custom';
           setIsCustomRole(isCustom);
           
           // In edit mode, always show permissions. In create mode, show only for custom
           if (!isEditMode) {
               setShowPermissions(isCustom);
           }
           
           // Load permissions for the selected predefined role
           if (!isCustom && value && isEditMode) {
               loadRolePermissions(parseInt(value));
           }
        }
    };
    
    const loadRolePermissions = async (roleId) => {
        try {
            const res = await axios.get(`${SERVER}/roles/${roleId}/permissions`, { withCredentials: true });
            setFormData(prev => ({ ...prev, custom_permissions: res.data.map(p => p.id) }));
        } catch (err) {
            console.error("Failed to load role permissions", err);
        }
    };

    const handlePermissionToggle = (permId) => {
        setFormData(prev => {
            const current = prev.custom_permissions;
            if (current.includes(permId)) {
                return { ...prev, custom_permissions: current.filter(id => id !== permId) };
            } else {
                return { ...prev, custom_permissions: [...current, permId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMsg({ type: '', text: '' });

        try {
            const payload = {
                email: formData.email,
                role_id: isCustomRole ? null : (formData.role_id === 'custom' ? null : formData.role_id),
                custom_permissions: formData.custom_permissions,
                custom_role_name: isCustomRole ? formData.custom_role_name : null,
                update_permissions: isEditMode && !isCustomRole // Flag to update predefined role permissions
            };
            
            // Only send password if provided (for edit) or required (create)
            if (formData.password) payload.password = formData.password;

            if (isEditMode) {
                await axios.put(`${SERVER}/admin/users/${editingId}`, payload, { withCredentials: true });
                setMsg({ type: 'success', text: 'Admin updated successfully' });
            } else {
                await axios.post(`${SERVER}/admin/create`, payload, { withCredentials: true });
                setMsg({ type: 'success', text: 'Admin created successfully' });
                setFormData({ email: '', password: '', role_id: '', custom_role_name: '', custom_permissions: [] });
            }
            fetchAdmins();
            setTimeout(() => setShowModal(false), 1500);
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || 'Operation failed' });
        } finally {
            setLoading(false);
        }
    };
    
    // Deletion
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
             await axios.delete(`${SERVER}/admin/${id}`, { withCredentials: true });
             fetchAdmins();
        } catch (err) {
             alert("Failed to delete");
        }
    }

    if (!isSuperAdmin()) return <div className="p-4">Access Denied</div>;

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header Card */}
            <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-3xl shadow-2xl p-8 animate-scaleIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
                <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                            <FaUserShield className="text-3xl text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-white mb-1">Admin Management</h2>
                            <p className="text-blue-100">Manage administrators and their permissions</p>
                        </div>
                    </div>
                    <button 
                        onClick={openCreateModal}
                        className="px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-white/30 flex items-center gap-2 group"
                    >
                        <FaPlus className="group-hover:rotate-90 transition-transform duration-300" />
                        <span>New Admin</span>
                    </button>
                </div>
            </div>

            {msg.text && !showModal && (
                <div className={`p-6 rounded-2xl shadow-lg flex items-center gap-3 animate-slideUp ${msg.type === 'error' ? 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${msg.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
                        <span className="text-white text-lg">{msg.type === 'error' ? '✕' : '✓'}</span>
                    </div>
                    <span className="font-semibold">{msg.text}</span>
                </div>
            )}

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-slate-200/50 animate-slideUp">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">📧 Email</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">👤 Role</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">📅 Created At</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">⚙️ Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100">
                            {admins.map((admin, index) => (
                                <tr key={admin.id} className="hover:bg-blue-50/50 transition-colors duration-200 group">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{admin.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-3 py-1.5 inline-flex text-xs font-bold rounded-full ${admin.role_name === 'Super Admin' ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'}`}>
                                            {admin.role_name || 'No Role'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                        {new Date(admin.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            <button 
                                                onClick={() => openEditModal(admin.id)} 
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                                                title="View/Edit Details"
                                            >
                                                <FaEye size={18} />
                                            </button>
                                            
                                            {admin.role_name !== 'Super Admin' && (
                                                <button 
                                                    onClick={() => handleDelete(admin.id)} 
                                                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-110"
                                                    title="Delete Admin"
                                                >
                                                    <FaTrash size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CREATE/EDIT MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] overflow-y-auto p-4 animate-fadeIn" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl my-8 animate-scaleIn max-h-[90vh] flex flex-col">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-center flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                    <FaUserShield className="text-2xl text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-white">
                                    {isEditMode ? 'Edit Admin & Permissions' : 'Create New Admin'}
                                </h3>
                            </div>
                            <button 
                                onClick={() => setShowModal(false)} 
                                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
                            >
                                <FaTimes className="text-white group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto flex-1">
                            {msg.text && (
                                <div className={`p-4 rounded-xl flex items-center gap-3 ${msg.type === 'error' ? 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 text-red-800' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${msg.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
                                        <span className="text-white text-sm">{msg.type === 'error' ? '✕' : '✓'}</span>
                                    </div>
                                    <span className="font-semibold text-sm">{msg.text}</span>
                                </div>
                            )}

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                    <span>📧</span>
                                    Email Address
                                </label>
                                <input 
                                    type="email" 
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    placeholder="admin@example.com"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                    <span>🔒</span>
                                    {isEditMode ? 'New Password (leave blank to keep current)' : 'Password'}
                                </label>
                                <input 
                                    type="password" 
                                    name="password"
                                    required={!isEditMode}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                    <span>👤</span>
                                    Assign Role
                                </label>
                                <select 
                                    name="role_id" 
                                    required={!isCustomRole}
                                    value={formData.role_id}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="">Select a Role</option>
                                    {roles.filter(r => !r.name.startsWith('Custom')).map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                    <option value="custom" className="font-bold">+ Create Custom Role</option>
                                </select>
                            </div>

                            {/* Custom Role Name Input */}
                            {isCustomRole && (
                                <div className="animate-slideUp">
                                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                                        <span>✨</span>
                                        Custom Role Name
                                    </label>
                                    <input 
                                        type="text" 
                                        name="custom_role_name"
                                        required={isCustomRole}
                                        value={formData.custom_role_name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Marketing Manager, Sales Admin"
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    />
                                </div>
                            )}

                            {/* Permissions Selector - Show for custom roles or in edit mode */}
                            {showPermissions && (
                                <div className="border-2 border-blue-200 rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50 max-h-96 overflow-y-auto scrollbar-thin animate-slideUp">
                                    <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                                        <span className="text-xl">🔐</span>
                                        {isCustomRole ? 'Select Accessible Modules' : 'View/Edit Permissions'}
                                    </h4>
                                    <p className="text-xs text-slate-600 mb-6 bg-white/60 p-3 rounded-xl">
                                        {isCustomRole 
                                            ? 'Check the permissions this user should have.' 
                                            : 'Modify permissions for this role. Changes will affect this admin only.'}
                                    </p>
                                    {Object.entries(permissions).map(([module, perms]) => (
                                        <div key={module} className="mb-6 last:mb-0">
                                            <h5 className="text-sm font-bold text-slate-700 uppercase mb-3 pb-2 border-b-2 border-blue-300 flex items-center gap-2">
                                                <span className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-xs">
                                                    {module.charAt(0)}
                                                </span>
                                                {module}
                                            </h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {perms.map(perm => (
                                                    <label key={perm.id} className="flex items-center space-x-3 text-sm text-slate-700 cursor-pointer p-2 rounded-lg hover:bg-white/60 transition-colors group">
                                                        <input 
                                                            type="checkbox"
                                                            checked={formData.custom_permissions.includes(perm.id)}
                                                            onChange={() => handlePermissionToggle(perm.id)}
                                                            className="w-5 h-5 rounded-lg text-blue-600 focus:ring-2 focus:ring-blue-500 border-2 border-slate-300"
                                                        />
                                                        <span className="group-hover:text-blue-600 transition-colors font-medium">{perm.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </form>
                        
                        <div className="p-6 bg-slate-50 border-t-2 border-slate-200 flex-shrink-0">
                            <div className="flex justify-end gap-4">
                                <button 
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-3 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl font-semibold transition-all duration-200"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{isEditMode ? 'Update Admin' : 'Create Admin'}</span>
                                            <span>→</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManagement;
